import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { FaUpload, FaDownload, FaTrash, FaEye, FaEdit, FaTimes, FaSpinner, FaSave, FaFileAlt, FaPhone, FaEnvelope, FaCalendar, FaPaperPlane, FaBan } from 'react-icons/fa';
import { MdOutlineRefresh } from "react-icons/md";
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import { useToast } from '../../contexts/ToastContext';
import { uploadTranscript, getTranscripts, deleteTranscript, getTranscriptById, updateTranscriptMetadata, saveTranscriptToDatabase, startTranscriptAnalysis, getCachedTranscript, getCachedAnalysis } from '../../services/transcriptService';
import { getContacts } from '../../services/radar';
import { sendFeatureNotificationRequest } from '../../services/notifications';
import websocketService from '../../services/websocketService';
import hubspotService from '../../services/hubspot';

const RadarTranscriptAnalyzer = memo(({ 
  accounts, 
  contacts, 
  industries, 
  dealStages,
  transcriptCompanyId: propTranscriptCompanyId = '',
  onTranscriptCompanyChange = null,
  transcriptContactNames: propTranscriptContactNames = '',
  onTranscriptContactChange = null
}) => {
  const { user } = useAuth();
  const { planType } = usePlan();
  const { showSuccess, showError } = useToast();
  
  // Transcript Analyzer state
  const [transcripts, setTranscripts] = useState([]);
  const [currentTranscript, setCurrentTranscript] = useState(null);
  const [deepAnalysisTranscript, setDeepAnalysisTranscript] = useState(null);
  const [isTranscriptUploading, setIsTranscriptUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeepAnalysis, setShowDeepAnalysis] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const [isLoadingTranscripts, setIsLoadingTranscripts] = useState(false);

  // Transcript upload metadata state - use props if provided, otherwise local state
  const [transcriptCompanyName, setTranscriptCompanyName] = useState('');
  const [transcriptContactNames, setTranscriptContactNames] = useState(propTranscriptContactNames || '');
  const [transcriptCompanyId, setTranscriptCompanyId] = useState(propTranscriptCompanyId || null);
  const [transcriptContactIds, setTranscriptContactIds] = useState([]);
  const [showTranscriptMetadataForm, setShowTranscriptMetadataForm] = useState(false);
  
  // Sync with props
  useEffect(() => {
    if (propTranscriptCompanyId !== undefined) {
      setTranscriptCompanyId(propTranscriptCompanyId || null);
      if (propTranscriptCompanyId && accounts && Array.isArray(accounts)) {
        const selectedCompany = accounts.find(acc => acc.id && acc.id.toString() === propTranscriptCompanyId.toString());
        setTranscriptCompanyName(selectedCompany ? (selectedCompany.companyName || selectedCompany.name) : '');
      } else {
        setTranscriptCompanyName('');
      }
    }
  }, [propTranscriptCompanyId, accounts]);

  // Debug: Log accounts data when it changes
  useEffect(() => {
    console.log('📊 Accounts data in RadarTranscriptAnalyzer:', {
      accounts,
      accountsType: typeof accounts,
      isArray: Array.isArray(accounts),
      length: accounts?.length || 0,
      firstAccount: accounts?.[0],
      firstAccountKeys: accounts?.[0] ? Object.keys(accounts[0]) : [],
      firstAccountId: accounts?.[0]?.id,
      firstAccountName: accounts?.[0]?.companyName || accounts?.[0]?.name,
      accountsWithIds: accounts?.filter(acc => acc.id)?.length || 0,
      accountsWithNames: accounts?.filter(acc => acc.companyName || acc.name)?.length || 0
    });
  }, [accounts]);
  
  useEffect(() => {
    if (propTranscriptContactNames !== undefined) {
      setTranscriptContactNames(propTranscriptContactNames);
    }
  }, [propTranscriptContactNames]);

  // Companies and contacts data
  const [companiesList, setCompaniesList] = useState([]);
  const [contactsList, setContactsList] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  
  // Dropdown visibility states
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);

  // Transcript table state
  const [transcriptSearchTerm, setTranscriptSearchTerm] = useState('');
  const [transcriptFilters, setTranscriptFilters] = useState({
    dateRange: 'all'
  });
  const [transcriptsPerPage] = useState(10);
  const [showAllTranscripts, setShowAllTranscripts] = useState(false);

  // Notification state
  const [sendingNotification, setSendingNotification] = useState(false);

  // Call confirmation state
  const [showCallConfirmation, setShowCallConfirmation] = useState(false);
  const [callConfirmationData, setCallConfirmationData] = useState({
    transcriptId: null,
    contactId: null,
    companyId: null,
    callTitle: '',
    callNotes: ''
  });

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);

  // Logging call state
  const [loggingCall, setLoggingCall] = useState(false);

  const fileInputRef = useRef(null);

  // Helper function to format analysis data
  const formatAnalysisData = (data) => {
    if (typeof data === 'string') {
      return data;
    }
    
    if (Array.isArray(data)) {
      return (
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          {data.map((item, index) => (
            <li key={index} style={{ marginBottom: '4px' }}>
              {typeof item === 'string' ? item : JSON.stringify(item)}
            </li>
          ))}
        </ul>
      );
    }
    
    if (typeof data === 'object' && data !== null) {
      return (
        <div>
          {Object.entries(data).map(([key, value]) => (
            <div key={key} style={{ marginBottom: '8px' }}>
              <strong style={{ textTransform: 'capitalize' }}>
                {key.replace(/([A-Z])/g, ' $1').trim()}:
              </strong>
              <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                {formatAnalysisData(value)}
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return JSON.stringify(data);
  };

  // Helper function to extract call duration in minutes from transcript analysis data
  const getCallDurationInMinutes = (transcript) => {
    // Default to 30 if no duration is available
    const defaultDuration = 30;
    
    console.log('🔍 Getting call duration for transcript:', {
      id: transcript?.id,
      hasAnalysisData: !!transcript?.analysisData,
      hasCallMetrics: !!transcript?.analysisData?.callMetrics,
      duration: transcript?.analysisData?.callMetrics?.duration,
      durationType: typeof transcript?.analysisData?.callMetrics?.duration
    });
    
    // Try to get duration from analysisData.callMetrics.duration
    if (transcript?.analysisData?.callMetrics?.duration) {
      const duration = transcript.analysisData.callMetrics.duration;
      
      // If it's already a number, assume it's in minutes
      if (typeof duration === 'number' && Number.isFinite(duration)) {
        const result = Math.round(duration);
        console.log('✅ Duration from number:', result);
        return result;
      }
      
      // If it's a string, parse it
      if (typeof duration === 'string') {
        const lowerDuration = duration.toLowerCase().trim();
        
        // Extract number from string (handle formats like "2.58 minutes", "3 min", "155 seconds", "2.58", etc.)
        // Try to match the first number (including decimals)
        const numberMatch = lowerDuration.match(/(\d+\.?\d*)/);
        if (numberMatch) {
          const number = parseFloat(numberMatch[1]);
          if (!isNaN(number) && number > 0) {
            let result;
            // Convert based on unit
            if (lowerDuration.includes('hour') || lowerDuration.includes('hr')) {
              result = Math.max(1, Math.round(number * 60));
            } else if (lowerDuration.includes('sec')) {
              result = Math.max(1, Math.round(number / 60)); // At least 1 minute
            } else {
              // Assume minutes if no unit specified or if 'min' is present
              // Round to nearest integer, but ensure at least 1 minute
              result = Math.max(1, Math.round(number));
            }
            console.log('✅ Duration from string:', duration, '->', result, 'minutes');
            return result;
          }
        }
      }
    }
    
    // Fallback: Try to calculate from transcript text if available (parse timestamps)
    // Transcript format: "0000.000 -- 0155.000" means 155 seconds
    if (transcript?.transcript || transcript?.fileBuffer) {
      const transcriptText = transcript.transcript || (transcript.fileBuffer ? transcript.fileBuffer.toString() : '');
      
      // Look for timestamp patterns like "0000.000 -- 0155.000" or "00:00:00 -- 00:02:35"
      const timestampPatterns = [
        // Pattern: "HHMMSS.SSS -- HHMMSS.SSS" or "MMSS.SSS -- MMSS.SSS"
        /(\d{2,6})\.(\d{3})\s*--\s*(\d{2,6})\.(\d{3})/,
        // Pattern: "HH:MM:SS -- HH:MM:SS"
        /(\d{1,2}):(\d{2}):(\d{2})\s*--\s*(\d{1,2}):(\d{2}):(\d{2})/,
        // Pattern: "MM:SS -- MM:SS"
        /(\d{1,2}):(\d{2})\s*--\s*(\d{1,2}):(\d{2})/
      ];
      
      let maxEndTime = 0;
      
      for (const pattern of timestampPatterns) {
        const matches = transcriptText.matchAll(new RegExp(pattern.source, 'g'));
        for (const match of matches) {
          let endTimeSeconds = 0;
          
          if (pattern === timestampPatterns[0]) {
            // Format: "0155.000" -> 155 seconds
            const endTimeStr = match[3] + '.' + match[4];
            endTimeSeconds = parseFloat(endTimeStr);
          } else if (pattern === timestampPatterns[1]) {
            // Format: "00:02:35" -> 155 seconds
            const hours = parseInt(match[4]) || 0;
            const minutes = parseInt(match[5]) || 0;
            const seconds = parseInt(match[6]) || 0;
            endTimeSeconds = hours * 3600 + minutes * 60 + seconds;
          } else if (pattern === timestampPatterns[2]) {
            // Format: "02:35" -> 155 seconds
            const minutes = parseInt(match[3]) || 0;
            const seconds = parseInt(match[4]) || 0;
            endTimeSeconds = minutes * 60 + seconds;
          }
          
          if (endTimeSeconds > maxEndTime) {
            maxEndTime = endTimeSeconds;
          }
        }
      }
      
      if (maxEndTime > 0) {
        const result = Math.max(1, Math.round(maxEndTime / 60)); // Convert to minutes, at least 1
        console.log('✅ Duration calculated from timestamps:', maxEndTime, 'seconds ->', result, 'minutes');
        return result;
      }
    }
    
    console.log('⚠️ Using default duration:', defaultDuration);
    return defaultDuration;
  };

  // Check if summary is fallback metadata (word count analysis)
  const isFallbackSummary = (summary) => {
    if (!summary || typeof summary !== 'string') return false;
    return summary.includes('words across') && summary.includes('sentences') && summary.includes('Average words per sentence');
  };

  // Get the best available summary content
  const getBestSummary = (transcript) => {
    // Priority 1: Real analysis summary from analysisData
    if (transcript.analysisData && transcript.analysisData.summary && !isFallbackSummary(transcript.analysisData.summary)) {
      return transcript.analysisData.summary;
    }
    
    // Priority 2: Transcript summary if it's not fallback metadata
    if (transcript.summary && !isFallbackSummary(transcript.summary)) {
      return transcript.summary;
    }
    
    // Priority 3: If we have analysis data but no summary, show a general message
    if (transcript.analysisData) {
      return 'Analysis completed. View detailed insights using the "View Deep Analysis" button.';
    }
    
    // Priority 4: If only fallback summary exists, show loading state
    if (transcript.summary && isFallbackSummary(transcript.summary)) {
      return null; // Will trigger loading state
    }
    
    return 'Processing...';
  };

  // Load companies and contacts
  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await hubspotService.getCompanies();
      setCompaniesList(response.data.companies || []);
    } catch (error) {
      console.error('Error loading companies:', error);
      setCompaniesList([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadContacts = async (companyName = null, searchTerm = '') => {
    try {
      setLoadingContacts(true);
      const params = {};
      if (companyName) {
        params.company = companyName;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const response = await getContacts(params);
      const contacts = response.contacts || [];
      console.log('🔍 Contacts loaded for upload section:', contacts.length, 'contacts');
      console.log('🔍 First few contacts:', contacts.slice(0, 3));
      setContactsList(contacts);
    } catch (error) {
      console.error(' Error loading contacts:', error);
      setContactsList([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Handle company selection
  const handleCompanySelect = (companyId) => {
    setTranscriptCompanyId(companyId);
    const selectedCompany = companiesList.find(c => c.id === companyId);
    setTranscriptCompanyName(selectedCompany ? selectedCompany.name : '');
    
    // Clear contact selection when company changes
    setTranscriptContactIds([]);
    setTranscriptContactNames('');
    
    // Reload contacts filtered by selected company
    if (selectedCompany) {
      loadContacts(selectedCompany.name);
    } else {
      loadContacts();
    }
  };

  // Handle contact selection
  const handleContactToggle = (contactId) => {
    const contact = contactsList.find(c => c.id === contactId);
    if (!contact) return;

    setTranscriptContactIds(prev => {
      const isSelected = prev.includes(contactId);
      let newIds;
      
      if (isSelected) {
        newIds = prev.filter(id => id !== contactId);
      } else {
        newIds = [...prev, contactId];
      }
      
      // Update contact names string
      const selectedContacts = contactsList.filter(c => newIds.includes(c.id));
      setTranscriptContactNames(selectedContacts.map(c => c.name).join(', '));
      
      return newIds;
    });
  };

  // Load transcripts on component mount
  useEffect(() => {
    if (user) {
      loadTranscripts();
      loadCompanies();
      loadContacts();
    }
  }, [user]);

  // Search effects
  useEffect(() => {
    if (companySearchTerm) {
      // Filter companies locally for now (could be moved to API)
      const filteredCompanies = companiesList.filter(company => 
        company.name.toLowerCase().includes(companySearchTerm.toLowerCase())
      );
      setCompaniesList(filteredCompanies);
    } else {
      loadCompanies();
    }
  }, [companySearchTerm]);

  useEffect(() => {
    const selectedCompany = companiesList.find(c => c.id === transcriptCompanyId);
    loadContacts(selectedCompany?.name, contactSearchTerm);
  }, [contactSearchTerm, transcriptCompanyId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownOpen && !event.target.closest('[data-dropdown="company"]')) {
        setCompanyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [companyDropdownOpen]);

  // Reload transcripts when showAllTranscripts changes
  useEffect(() => {
    if (user) {
      loadTranscripts();
    }
  }, [showAllTranscripts, user]);

  // Set up WebSocket handling for transcript updates
  useEffect(() => {
    const handleTranscriptUpdate = (message) => {
      if (!user) return;

      if (message.type === 'transcript_analysis_completed') {
        if (currentTranscript && currentTranscript.id === message.transcriptId) {
          getTranscriptById(message.transcriptId).then(response => {
            setCurrentTranscript(response.transcript);
          }).catch(err => {
            console.error('Error refreshing transcript:', err);
          });
        }
        loadTranscripts();
      } else if (message.type === 'transcript_analysis_failed') {
        showError('Transcript analysis failed. Please try again.');
        loadTranscripts();
      }
    };

    websocketService.on('transcript_analysis_completed', handleTranscriptUpdate);
    websocketService.on('transcript_analysis_failed', handleTranscriptUpdate);

    return () => {
      websocketService.off('transcript_analysis_completed', handleTranscriptUpdate);
      websocketService.off('transcript_analysis_failed', handleTranscriptUpdate);
    };
  }, [currentTranscript, user]);

  // Load data when switching to Transcript Analyzer view
  useEffect(() => {
    if (user) {
      loadTranscripts();
    }
  }, [user]);

  // Poll for transcript updates while analysis is in progress
  useEffect(() => {
    if (!currentTranscript) return;

    if (currentTranscript && currentTranscript.summary && currentTranscript.status === 'uploading') {
      const pollInterval = setInterval(async () => {
        try {
          const response = await getTranscriptById(currentTranscript.id);
          setCurrentTranscript(response.transcript);
        } catch (err) {
          console.error('Error refreshing transcript:', err);
        }
      }, 2000);

      return () => clearInterval(pollInterval);
    }

    if (currentTranscript && currentTranscript.status === 'analyzing') {
      const pollInterval = setInterval(async () => {
        try {
          if (currentTranscript && (currentTranscript.status === 'analyzing_complete' || currentTranscript.status === 'completed' || currentTranscript.status === 'failed' || currentTranscript.status === 'uploaded')) {
            clearInterval(pollInterval);
            return;
          }

          const response = await getCachedTranscript(currentTranscript.id);
          const updatedTranscript = response;

          const hasChanges = updatedTranscript.status !== (currentTranscript?.status || '') || 
                            (!currentTranscript?.summary && updatedTranscript.summary) ||
                            updatedTranscript.summary !== (currentTranscript?.summary || '') ||
                            updatedTranscript.updatedAt !== (currentTranscript?.updatedAt || '');

          if (hasChanges) {
            setCurrentTranscript(updatedTranscript);

            if (updatedTranscript.status === 'analyzing_complete' || updatedTranscript.status === 'completed') {
              try {
                if (updatedTranscript.status === 'analyzing_complete') {
                  const cachedAnalysis = await getCachedAnalysis(updatedTranscript.id);
                  setCurrentTranscript(prev => ({
                    ...prev,
                    ...cachedAnalysis,
                    status: 'analyzing_complete'
                  }));
                }
              } catch (err) {
                console.error('Error fetching cached analysis:', err);
              }
            } else if (updatedTranscript.status === 'failed') {
              showError('Transcript analysis failed. Please try again.');
            }
          }
        } catch (err) {
          console.error('Error polling transcript:', err);
        }
      }, 2000);

      return () => clearInterval(pollInterval);
    }
  }, [currentTranscript?.id, currentTranscript?.status]);

  // Data loading functions
  const loadTranscripts = async () => {
    try {
      if (!user) {
        console.log('🔒 User not authenticated, skipping transcript load');
        return;
      }
      
      setIsLoadingTranscripts(true);
      console.log('🔍 Loading transcripts');
      const response = await getTranscripts({ 
        limit: 1000
      });
      
      console.log('📊 Transcripts API response:', response);
      setTranscripts(response.transcripts || []);
    } catch (err) {
      console.error(' Error loading transcripts:', err);
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        console.log('🔒 Authentication error - user may not be logged in');
        setTranscripts([]);
      }
    } finally {
      setIsLoadingTranscripts(false);
    }
  };

  // Transcript Analyzer handlers
  const handleTranscriptUpload = async (file) => {
    console.log('=== handleTranscriptUpload called ===');
    console.log('File:', file.name, 'Size:', file.size);
    
    // Validate file
    if (!file) return;
    
    const validTypes = ['.txt', '.docx', '.pdf', '.vtt'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(fileExtension)) {
      showError('Please upload a valid file type (.txt, .docx, .pdf, .vtt)', {
        title: 'Invalid File Type',
        duration: 5000
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      showError('File size must be less than 10MB', {
        title: 'File Too Large',
        duration: 5000
      });
      return;
    }

    console.log('Before state reset - currentTranscript:', currentTranscript);
    console.log('Before state reset - isTranscriptUploading:', isTranscriptUploading);

    // Clear any previous transcript and start fresh
    setCurrentTranscript(null);
    setIsTranscriptUploading(true);
    // Force a re-render by incrementing the upload key
    setUploadKey(prev => prev + 1);

    console.log('After state reset - currentTranscript should be null');
    console.log('After state reset - isTranscriptUploading should be true');
    console.log('After state reset - uploadKey incremented');

    try {
      // Prepare metadata for upload
      const metadata = {};
      if (transcriptCompanyName.trim()) {
        metadata.companyName = transcriptCompanyName.trim();
      }
      if (transcriptContactNames.trim()) {
        metadata.contactNames = transcriptContactNames.split(',').map(name => name.trim()).filter(Boolean);
      }

      // Upload transcript to backend with metadata
      const response = await uploadTranscript(file, metadata);

      if (response.transcript) {
        console.log('=== Upload response received ===');
        console.log('Response transcript:', response.transcript);
        
        // Set current transcript to show summary (but don't add to table)
        setCurrentTranscript(response.transcript);
        
        console.log('Current transcript set, should now show summary');

        // Clear metadata form after successful upload
        setTranscriptCompanyName('');
        setTranscriptContactNames('');
        setTranscriptCompanyId(null);
        setTranscriptContactIds([]);

        // Immediately start polling for updates to get the real status and summary
        setTimeout(async () => {
          try {
            const updatedResponse = await getTranscriptById(response.transcript.id);
            const updatedTranscript = updatedResponse.transcript;
            
            // Update only the current transcript (don't add to table)
            setCurrentTranscript(updatedTranscript);
            
          } catch (err) {
            console.error('Error refreshing transcript after upload:', err);
          }
        }, 1000); // Wait 1 second for backend to process
      }

    } catch (error) {
      console.error('Transcript upload error:', error);
      showError('Failed to upload transcript. Please try again.');
    } finally {
      setIsTranscriptUploading(false);
    }
  };

  const handleViewTranscript = async (transcript) => {
    try {
      setDeepAnalysisTranscript({ ...transcript, isLoading: true });
      setShowDeepAnalysis(true);
      const response = await getTranscriptById(transcript.id);
      const completeTranscript = response.transcript;
      setDeepAnalysisTranscript(completeTranscript);
    } catch (error) {
      console.error('Error fetching transcript details:', error);
      showError('Failed to fetch transcript details. Please try again.', {
        title: 'Fetch Error',
        duration: 5000
      });
      setDeepAnalysisTranscript(transcript);
    }
  };

  const handleDeleteTranscript = async (transcriptId) => {
    try {
      await deleteTranscript(transcriptId);
      setTranscripts(prev => prev.filter(t => t.id !== transcriptId));
      if (currentTranscript?.id === transcriptId) {
        setCurrentTranscript(null);
      }
      showSuccess('Transcript deleted successfully', {
        title: 'Success',
        duration: 3000
      });
    } catch (err) {
      console.error('Error deleting transcript:', err);
      showError('Failed to delete transcript', {
        title: 'Delete Error',
        duration: 5000
      });
    }
  };

  const handleStartAnalysis = async (transcript) => {
    try {
      console.log('📋 Transcript data:', {
        id: transcript.id,
        status: transcript.status,
        originalFileName: transcript.originalFileName
      });

      setIsAnalyzing(true);

      await startTranscriptAnalysis(transcript.id);
      setCurrentTranscript({ ...transcript, status: 'analyzing' });
      showSuccess('Analysis started successfully');
    } catch (error) {
      console.error('Error starting analysis:', error);
      showError('Failed to start analysis. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSaveAnalysis = async (transcript) => {
    try {
      console.log('📋 Transcript data:', {
        id: transcript.id,
        status: transcript.status,
        companyName: transcript.companyName,
        contactNames: transcript.contactNames,
        companyId: transcript.companyId,
        contactIds: transcript.contactIds
      });

      setIsSaving(true);

      const response = await saveTranscriptToDatabase(transcript.id, {
        companyName: transcript.companyName,
        contactNames: transcript.contactNames,
        companyId: transcript.companyId,
        contactIds: transcript.contactIds,
        status: 'saved'
      });
      
      if (response && response.id) {
        showSuccess('Transcript saved successfully');
        
        // Refresh the transcripts list
        await loadTranscripts();
        
        // Update current transcript if it's the same one
        if (currentTranscript?.id === transcript.id) {
          setCurrentTranscript(prev => ({
            ...prev,
            status: 'saved'
          }));
        }
      } else {
        throw new Error('Failed to save transcript');
      }
    } catch (error) {
      console.error('Error saving transcript:', error);
      showError('Failed to save transcript. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Inline editing handlers for transcript table
  const handleStartEdit = (transcriptId, field, value) => {
    setEditingCell({ transcriptId, field });
    setEditValue(value || '');
  };

  const handleSaveEdit = async (transcriptId, field) => {
    try {
      setSavingCell({ transcriptId, field });
      
      const transcript = transcripts.find(t => t.id === transcriptId);
      if (!transcript) return;

      const metadata = {
        companyName: field === 'companyName' ? editValue : transcript.companyName,
        contactNames: field === 'companyName' && editValue !== transcript.companyName 
          ? [] // Clear contact names if company is being changed
          : field === 'contactNames' 
            ? editValue.split(',').map(name => name.trim()).filter(Boolean) 
            : transcript.contactNames
      };

      await updateTranscriptMetadata(transcriptId, metadata);
      
      // Update local state
      setTranscripts(prev => prev.map(t => 
        t.id === transcriptId 
          ? { ...t, ...metadata }
          : t
      ));

      // Update current transcript if it's the one being edited
      if (currentTranscript?.id === transcriptId) {
        setCurrentTranscript(prev => ({ ...prev, ...metadata }));
      }

      setEditingCell(null);
      setEditValue('');
      // Metadata updated successfully

    } catch (error) {
      console.error('Error updating transcript metadata:', error);
      showError('Failed to update transcript metadata', {
        title: 'Update Error',
        duration: 5000
      });
    } finally {
      setSavingCell(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleLogCallToHubSpot = async (transcriptId, contactId, companyId) => {
    try {
      setLoggingCall(true);
      
      const callData = {
        transcriptId,
        contactId,
        companyId,
        callTitle: callConfirmationData.callTitle,
        callNotes: callConfirmationData.callNotes
      };

      const response = await hubspotService.logCall(callData);
      
      if (response.success) {
        showSuccess('Call logged successfully in HubSpot!', {
          title: 'HubSpot Integration',
          duration: 3000
        });
        setShowCallConfirmation(false);
      } else {
        throw new Error(response.message || 'Failed to log call');
      }
    } catch (error) {
      console.error('Error logging call:', error);
      showError(error.response?.data?.message || 'Failed to log call to HubSpot', {
        title: 'HubSpot Integration Error',
        duration: 5000
      });
    } finally {
      setLoggingCall(false);
    }
  };

  const filteredTranscripts = transcripts.filter(transcript => {
    // If no search term and no date filter, show all transcripts
    if (!transcriptSearchTerm && !transcriptFilters.dateRange) {
      return true;
    }
    
    // Check search filter
    // Normalize contact names to an array to avoid runtime errors
    const contactNamesArray = Array.isArray(transcript.contactNames)
      ? transcript.contactNames
      : typeof transcript.contactNames === 'string'
        ? transcript.contactNames.split(',').map(name => name.trim()).filter(Boolean)
        : [];

    const matchesSearch = !transcriptSearchTerm || 
                         (transcript.originalFileName && transcript.originalFileName.toLowerCase().includes(transcriptSearchTerm.toLowerCase())) ||
                         (transcript.summary && transcript.summary.toLowerCase().includes(transcriptSearchTerm.toLowerCase())) ||
                         (transcript.companyName && transcript.companyName.toLowerCase().includes(transcriptSearchTerm.toLowerCase())) ||
                         (contactNamesArray.length > 0 && contactNamesArray.some(name => name.toLowerCase().includes(transcriptSearchTerm.toLowerCase())));
    
    // Check date filter
    const matchesDate = !transcriptFilters.dateRange || (() => {
      const uploadDate = new Date(transcript.createdAt);
      const now = new Date();
      const oneDay = 24 * 60 * 60 * 1000;
      
      switch (transcriptFilters.dateRange) {
        case 'today':
          return uploadDate.toDateString() === now.toDateString();
        case 'week':
          return (now - uploadDate) <= 7 * oneDay;
        case 'month':
          return (now - uploadDate) <= 30 * oneDay;
        default:
          return true;
      }
    })();
    
    return matchesSearch && matchesDate;
  });

  const paginatedTranscripts = showAllTranscripts 
    ? filteredTranscripts 
    : filteredTranscripts.slice(0, transcriptsPerPage);

  const handleFeatureNotificationRequest = async () => {
    try {
      setSendingNotification(true);
      const response = await sendFeatureNotificationRequest({
        feature: 'Buyer Cards',
        email: user?.email,
        message: 'I am interested in the Buyer Cards feature. Please notify me when it becomes available.'
      });
      
      if (response.success) {
        showSuccess('Thank you! We\'ll notify you when the Buyer Cards feature is available.');
      } else {
        throw new Error(response.message || 'Failed to send notification request');
      }
    } catch (error) {
      console.error('Error sending notification request:', error);
      showError('Failed to send notification request. Please try again.');
    } finally {
      setSendingNotification(false);
    }
  };

  // Render the component
  if (!user) {
    return (
      <div className="radar-content" style={{ padding: '16px' }}>
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px', 
          color: '#6b7280' 
        }}>
          <p style={{ margin: '0', fontSize: '14px' }}>
            Please log in to access the Transcript Analyzer.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="radar-content-transcript-analyzer">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        id="transcript-file-input"
        type="file"
        accept=".txt,.docx,.pdf,.vtt"
        onChange={(e) => {
          console.log('=== File input onChange triggered ===');
          console.log('Event object:', e);
          console.log('Event target:', e.target);
          console.log('Event target files:', e.target.files);
          console.log('Event target files length:', e.target.files?.length);
          
          const file = e.target.files?.[0];
          console.log('Selected file:', file);
          
          if (file) {
            console.log('File details:', {
              name: file.name,
              size: file.size,
              type: file.type,
              lastModified: file.lastModified
            });
            console.log('Calling handleTranscriptUpload with file:', file.name);
            handleTranscriptUpload(file);
          } else {
            console.log('No file selected or files array is empty');
          }
        }}
        onClick={() => console.log('=== File input clicked directly ===')}
        onFocus={() => console.log('=== File input focused ===')}
        onBlur={() => console.log('=== File input blurred ===')}
        style={{ display: 'none' }}
      />

      {/* Top Section: Upload/Filters (Left) and Summary (Right) */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row',
        gap: '20px', 
        marginBottom: '20px',
        alignItems: 'stretch'
      }}>
        {/* Left Section: Upload and Filters (Stacked Vertically) */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '16px',
          flex: '0 0 360px',
          maxWidth: '360px',
          minHeight: '240px'
        }}>
          {/* Upload Section */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center', 
            gap: '2px',
            backgroundColor: '#f9fafb',
            padding: '16px',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            width: '100%',
            minHeight: '120px'
          }}
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = '#2563eb';
          e.currentTarget.style.background = '#eff6ff';
        }}
        onDragLeave={(e) => {
          e.currentTarget.style.borderColor = '#d1d5db';
          e.currentTarget.style.background = '#f9fafb';
        }}
        onDrop={(e) => {
          console.log('=== Drag and drop triggered ===');
          e.preventDefault();
          e.currentTarget.style.borderColor = '#d1d5db';
          e.currentTarget.style.background = '#f9fafb';
          const files = e.dataTransfer.files;
          console.log('Dropped files:', files);
          if (files.length > 0) {
            console.log('Calling handleTranscriptUpload with dropped file:', files[0].name);
            handleTranscriptUpload(files[0]);
          }
        }}
        onClick={() => {
          console.log('=== Upload button clicked ===');
          console.log('File input ref:', fileInputRef.current);
          
          if (fileInputRef.current) {
            // Clear any existing files first
            fileInputRef.current.value = '';
            // Trigger the file picker
            fileInputRef.current.click();
            console.log('File picker should now open');
          } else {
            console.error('File input ref not found!');
          }
          
          // File input clicked
        }}
        >
          {/* File Upload Icon */}
          <div className="radar-content-top-upload-button" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: '40px',
            minHeight: '40px'
          }}>
            <FaUpload style={{ fontSize: '1.5rem', color: '#9ca3af' }} />
          </div>
          {/* Description Text */}
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0', color: '#6b7280', fontSize: '14px', fontWeight: '500', lineHeight: '1.3' }}>
              Upload your call transcript
            </p>
            <p style={{ margin: '4px 0 0 0', color: '#9ca3af', fontSize: '12px' }}>
              Supports .txt, .docx, .pdf, .vtt (Max 10MB)
            </p>
          </div>
        </div>

        {/* Company and Contact Filters (Below Upload) */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
          flex: 1
        }}>
          {/* Company Selection */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              color: '#374151', 
              marginBottom: '4px'
            }}>
              Select Company (Optional)
            </label>
            <select
              value={transcriptCompanyId || ''}
              onChange={(e) => {
                const selectedCompany = accounts.find(acc => acc.id.toString() === e.target.value);
                const newCompanyId = e.target.value || null;
                setTranscriptCompanyId(newCompanyId);
                setTranscriptCompanyName(selectedCompany ? (selectedCompany.companyName || selectedCompany.name) : '');
                
                // Clear selected contact when company changes
                setTranscriptContactIds([]);
                setTranscriptContactNames('');
                
                // Update parent if callback provided
                if (onTranscriptCompanyChange) {
                  onTranscriptCompanyChange(newCompanyId);
                }
                if (onTranscriptContactChange) {
                  onTranscriptContactChange('');
                }
              }}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '12px',
                backgroundColor: 'white',
                minHeight: '36px',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Select Company --</option>
              {accounts.length > 0 ? (
                [...accounts]
                  .sort((a, b) => {
                    const nameA = (a.companyName || a.name || '').toLowerCase();
                    const nameB = (b.companyName || b.name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  .map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.companyName || account.name || 'Unnamed Company'}
                    </option>
                  ))
              ) : (
                <option value="" disabled>No companies available</option>
              )}
            </select>
          </div>

          {/* Contact Selection */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              color: '#374151', 
              marginBottom: '4px' 
            }}>
              Select Contact (Optional)
            </label>
            {(() => {
              // Calculate available contacts for the selected company
              const selectedCompany = (transcriptCompanyId && accounts && Array.isArray(accounts)) 
                ? accounts.find(acc => acc.id && acc.id.toString() === transcriptCompanyId.toString()) 
                : null;
              const selectedCompanyName = selectedCompany ? (selectedCompany.companyName || selectedCompany.name) : '';
              const availableContacts = contactsList.filter(contact => {
                if (!transcriptCompanyId) return false;
                return contact.company === selectedCompanyName || contact.companyName === selectedCompanyName;
              });
              const hasAvailableContacts = availableContacts.length > 0;
              const isDisabled = !transcriptCompanyId || !hasAvailableContacts;
              
              // Get the current selected contact name (use first if multiple)
              const currentContactName = transcriptContactNames ? transcriptContactNames.split(', ')[0] : '';
              
              return (
                <>
                  <select
                    value={currentContactName || ''}
                    onChange={(e) => {
                      const selectedContactName = e.target.value || null;
                      setTranscriptContactNames(selectedContactName || '');
                      if (onTranscriptContactChange) {
                        onTranscriptContactChange(selectedContactName || '');
                      }
                    }}
                    disabled={isDisabled}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '12px',
                      backgroundColor: isDisabled ? '#f3f4f6' : 'white',
                      minHeight: '36px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.6 : 1
                    }}
                  >
                    <option value="">-- Select Contact --</option>
                    {availableContacts.length > 0 ? (
                      [...availableContacts]
                        .sort((a, b) => {
                          const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
                          const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
                          return nameA.localeCompare(nameB);
                        })
                        .map((contact) => {
                          const contactName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email || 'Unnamed Contact';
                          return (
                            <option key={contact.id} value={contactName}>
                              {contactName}
                            </option>
                          );
                        })
                    ) : (
                      <option value="" disabled>No contacts available</option>
                    )}
                  </select>
                </>
              );
            })()}
           </div>
        </div>
        </div>

        {/* Right Section: Summary Display */}
        <div className="radar-content-top-transcript-summary" style={{ 
          flex: 1, 
          borderRadius: '12px', 
          padding: '16px',
          minHeight: '240px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: 0
        }}>
        
        {(() => {
          console.log('=== Conditional Rendering Check ===');
          console.log('currentTranscript:', currentTranscript);
          console.log('isTranscriptUploading:', isTranscriptUploading);
          console.log('Condition result:', !!(currentTranscript && !isTranscriptUploading));
          console.log('Should show summary:', !!(currentTranscript && !isTranscriptUploading));
          console.log('Should show upload form:', !(currentTranscript && !isTranscriptUploading));
          return null;
        })()}
        
        {/* Key: {uploadKey} - {currentTranscript && !isTranscriptUploading ? 'SHOWING SUMMARY' : 'SHOWING UPLOAD FORM'} */}
        {currentTranscript && !isTranscriptUploading ? (
          <div>
              <div style={{ 
              background: '#f8fafc',
              padding: '6px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              maxHeight: '160px'
            }}>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                {currentTranscript.originalFileName || currentTranscript.fileName}
              </h4>
              
              {/* Status Indicator - Calculated from actual data */}
              <div style={{ 
                marginBottom: '8px',
                display: 'flex', 
                alignItems: 'center',
                gap: '6px'
              }}>
                {(() => {
                  // Calculate actual status based on data, not database status field
                  let actualStatus = 'pending';
                  let statusText = '⏳ Uploading';
                  let backgroundColor = '#f1f5f9';
                  let textColor = '#64748b';
                  
                  if (currentTranscript.analysisData && currentTranscript.summary) {
                    actualStatus = 'completed';
                    statusText = ' Analysis Complete';
                    backgroundColor = '#dcfce7';
                    textColor = '#166534';
                  } else if (currentTranscript.status === 'failed') {
                    actualStatus = 'failed';
                    statusText = ' Analysis Failed';
                    backgroundColor = '#fee2e2';
                    textColor = '#dc2626';
                  } else if (currentTranscript.summary && !currentTranscript.analysisData) {
                    actualStatus = 'partial';
                    statusText = '⚠️ Partial Analysis';
                    backgroundColor = '#fef3c7';
                    textColor = '#92400e';
                  } else if (currentTranscript.status === 'analyzing' || currentTranscript.status === 'uploading') {
                    actualStatus = 'analyzing';
                    statusText = '🔄 Analyzing...';
                    backgroundColor = '#fef3c7';
                    textColor = '#92400e';
                  } else if (currentTranscript.status === 'uploaded') {
                    actualStatus = 'uploaded';
                    statusText = '📁 Uploaded - Ready to Analyze';
                    backgroundColor = '#e0f2fe';
                    textColor = '#0369a1';
                  }
                  
                  return (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      backgroundColor: backgroundColor,
                      color: textColor,
                      maxWidth: '100%',
                      wordBreak: 'break-word'
                    }}>
                      {statusText}
                    </span>
                  );
                })()}
              </div>
              
              <div style={{ 
                margin: '0 0 12px 0', 
                fontSize: '13px', 
                color: '#475569',
                lineHeight: '1.5'
              }}>
                {(() => {
                  const bestSummary = getBestSummary(currentTranscript);
                  
                  // If bestSummary is null, it means we have fallback summary - show loading
                  if (bestSummary === null) {
                    return (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        color: '#6b7280',
                        fontStyle: 'italic'
                      }}>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '12px' }} />
                        Analysis in progress... Real insights coming soon.
                      </div>
                    );
                  }
                  
                  return bestSummary;
                })()}
              </div>
              


              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
                  <button
                  onClick={() => {
                    // Set the current transcript as the deep analysis transcript
                    setDeepAnalysisTranscript(currentTranscript);
                    setShowDeepAnalysis(true);
                    // Opening deep analysis
                  }}
                    style={{
                    background: '#2563eb',
                      color: 'white',
                      border: 'none',
                    padding: '3px 6px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#1d4ed8'}
                  onMouseOut={(e) => e.target.style.background = '#2563eb'}
                >
                  View Deep Analysis
                  </button>
                
                {/* Analyze Button - Only show when transcript is uploaded but not analyzed */}
                {currentTranscript.status === 'uploaded' && (
                  <button
                    onClick={() => {
                      console.log('🔄 Analyze button clicked');
                      console.log('Current transcript:', currentTranscript);
                      handleStartAnalysis(currentTranscript);
                    }}
                    disabled={isAnalyzing}
                    style={{
                      background: isAnalyzing ? '#6b7280' : '#f59e0b',
                      color: 'white',
                      border: 'none',
                      padding: '3px 6px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '500',
                      cursor: isAnalyzing ? 'default' : 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      if (!isAnalyzing) {
                        e.target.style.background = '#d97706';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isAnalyzing) {
                        e.target.style.background = '#f59e0b';
                      }
                    }}
                  >
                    {isAnalyzing ? '🔄 Analyzing...' : '🔍 Analyze'}
                  </button>
                )}
                
                {/* Save Button - Only show when analysis is complete */}
                {currentTranscript.status === 'analyzing_complete' && (
                  <button
                    onClick={() => {
                      console.log('🔄 Save Analysis button clicked');
                      console.log('Current transcript:', currentTranscript);
                      console.log('Transcript company ID:', transcriptCompanyId);
                      console.log('Transcript company name:', transcriptCompanyName);
                      console.log('Transcript contact IDs:', transcriptContactIds);
                      console.log('Transcript contact names:', transcriptContactNames);
                      
                      // Get the selected company object
                      const selectedCompanyObj = (accounts && Array.isArray(accounts) && transcriptCompanyId != null) 
                        ? accounts.find(acc => {
                            if (!acc || acc.id == null) return false;
                            const accIdStr = acc.id.toString();
                            const transcriptCompanyIdStr = transcriptCompanyId.toString();
                            return accIdStr === transcriptCompanyIdStr;
                          })
                        : null;
                      
                      // Get the selected contact objects
                      const selectedContactObjs = contactsList.filter(contact => transcriptContactIds.includes(contact.id));
                      
                      console.log('Selected company object:', selectedCompanyObj);
                      console.log('Selected contact objects:', selectedContactObjs);
                      
                      // Debug: Check if company data is being set correctly
                      console.log('🔍 Debug company data:');
                      console.log('- transcriptCompanyId:', transcriptCompanyId);
                      console.log('- transcriptCompanyName:', transcriptCompanyName);
                      console.log('- selectedCompanyObj:', selectedCompanyObj);
                      console.log('- selectedCompanyObj?.companyName:', selectedCompanyObj?.companyName);
                      console.log('- selectedCompanyObj?.name:', selectedCompanyObj?.name);
                      
                      // Create a transcript object with current selections
                      const transcriptWithSelections = {
                        ...currentTranscript,
                        companyName: transcriptCompanyName || currentTranscript.companyName,
                        contactNames: transcriptContactNames ? transcriptContactNames.split(', ') : currentTranscript.contactNames,
                        companyId: transcriptCompanyId || currentTranscript.companyId,
                        contactIds: transcriptContactIds || currentTranscript.contactIds
                      };
                      
                      console.log('Final transcript with selections:', transcriptWithSelections);
                      
                      handleSaveAnalysis(transcriptWithSelections);
                    }}
                    disabled={currentTranscript.status === 'saved' || isSaving}
                    style={{
                      background: currentTranscript.status === 'saved' || isSaving ? '#6b7280' : '#10b981',
                      color: 'white',
                      border: 'none',
                      padding: '3px 6px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '500',
                      cursor: currentTranscript.status === 'saved' || isSaving ? 'default' : 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      if (currentTranscript.status !== 'saved' && !isSaving) {
                        e.target.style.background = '#059669';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (currentTranscript.status !== 'saved' && !isSaving) {
                        e.target.style.background = '#10b981';
                      }
                    }}
                  >
                    {isSaving ? (
                      <>
                        Saving...
                      </>
                    ) : currentTranscript.status === 'saved' ? (
                      '✓ Saved'
                    ) : (
                      'Save Analysis'
                    )}
                  </button>
                )}
              </div>
              </div>
            </div>
          ) : (
          <>
            {console.log('=== RENDERING UPLOAD FORM SECTION ===')}
            
            <div style={{ 
              background: '#f8fafc',
              padding: '12px 10px',
              borderRadius: '8px',
              border: '0.5px solid #cbd5e1',
              textAlign: 'center',
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                {isTranscriptUploading ? (
                  <>
                    <div style={{ fontSize: '1.5rem', color: '#2563eb' }}>🔄</div>
                    <p style={{ margin: '0', color: '#64748b', fontSize: '13px' }}>
                      Uploading transcript... Please wait
                    </p>
                  </>
                ) : (
                  <>
                    <FaFileAlt style={{ fontSize: '1.5rem', color: '#94a3b8' }} />
                    <p style={{ margin: '0', color: '#64748b', fontSize: '13px' }}>
                      Upload a transcript to see the Velox-generated summary
                    </p>
                  </>
          )}
        </div>
            </div>
          </>
      )}
        </div>
      </div>

    {/* Bottom Section - Previous Transcripts Table */}
    <div style={{ 
      background: '#ffffff',
      borderRadius: '12px',
      padding: '5px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
      width: '100%',
      overflow: 'hidden',
      height: '250px',
      marginLeft: 'auto',
      marginRight: 'auto',
      marginTop: '50px',
      alignContent: 'center',
      justifyContent: 'center'
      }}>
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      marginBottom: '8px'
      }}>
        <h3 style={{ margin: '0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
        Previous Transcripts
        </h3>
          <button
          onClick={() => {
            loadTranscripts();
            // Refreshing transcripts
          }}
          style={{
          color: '#1f2937',
          border: 'none',
          padding: '6px 10px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'background 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
          }}
          onMouseOver={(e) => e.target.style.background = 'white'}
          onMouseOut={(e) => e.target.style.background = 'white'}
          title="Refresh transcripts"
        >
          🔄 Refresh
            </button>
          </div>
      
      {/* Search and Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '8px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: '250px' }}>
          <input
            type="text"
            placeholder="Search transcripts..."
            value={transcriptSearchTerm}
            onChange={(e) => {
              setTranscriptSearchTerm(e.target.value);
              // Searching transcripts
            }}
            style={{
              width: '100%',
              padding: '6px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px'
            }}
          />
        </div>

        {/* Date Range Filter */}
        <select
          value={transcriptFilters.dateRange}
                          onChange={(e) => {
              setTranscriptFilters(prev => ({ ...prev, dateRange: e.target.value }));
              // Changing filters
            }}
          style={{
            padding: '6px 10px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '13px',
            minWidth: '120px'
          }}
        >
          <option value="">All Time</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>
        
        {/* Show All Toggle */}
        <button
          onClick={() => {
            setShowAllTranscripts(!showAllTranscripts);
            // Toggling show all
          }}
          style={{
            padding: '6px 10px',
            background: showAllTranscripts ? '#2563eb' : '#fff',
            color: showAllTranscripts ? '#fff' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '13px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          title={showAllTranscripts ? 'Show paginated view' : 'Show all transcripts'}
        >
          {showAllTranscripts ? 'Show Top 10' : 'Show All'}
        </button>
        
      </div>
      
      {isLoadingTranscripts ? (
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px', 
          color: '#6b7280'
        }}>
          <FaSpinner style={{ 
            animation: 'spin 1s linear infinite', 
            fontSize: '32px', 
            color: '#3b82f6',
            marginBottom: '16px'
          }} />
          <p style={{ margin: '0', fontSize: '14px' }}>
            Loading Transcripts...
          </p>
        </div>
      ) : filteredTranscripts.length > 0 ? (
        <>
          {/* Results Summary */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            fontSize: '13px',
            color: '#6b7280'
          }}>
            <span>
              Showing {paginatedTranscripts.length} of {filteredTranscripts.length} transcripts
            </span>
          </div>
          
        <div style={{ 
          overflowX: 'auto',
          overflowY: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '100%',
          maxHeight: '400px',
          WebkitOverflowScrolling: 'touch'
        }}>
          <table style={{ 
            width: '100%', 
            minWidth: '1000px',
            borderCollapse: 'collapse',
            overflowY: 'auto'
          }}>
              <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', minWidth: '100px', verticalAlign: 'top' }}>Date</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', minWidth: '150px', verticalAlign: 'top' }}>File Name</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', width: '200px', verticalAlign: 'top' }}>Company</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', width: '200px', verticalAlign: 'top' }}>Contacts</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', minWidth: '200px', verticalAlign: 'top' }}>Summary</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', minWidth: '200px', width: '200px', verticalAlign: 'top' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTranscripts.map((transcript) => {
                // Debug log to see transcript structure
                console.log('🔍 Transcript data:', {
                  id: transcript.id,
                  summary: transcript.summary,
                  analysisData: transcript.analysisData,
                  status: transcript.status,
                  companyName: transcript.companyName,
                  contactNames: transcript.contactNames
                });
                
                return (
                <tr key={transcript.id} style={{ borderBottom: '1px solid #f3f4f6', minHeight: '50px' }}>
                <td style={{ padding: '8px 10px', fontSize: '12px', color: '#6b7280', verticalAlign: 'top' }}>
                  {new Date(transcript.createdAt).toLocaleDateString()}
                </td>
                <td style={{ padding: '8px 10px', fontSize: '13px', color: '#1f2937', fontWeight: '500', verticalAlign: 'top' }}>
                  <div style={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    maxWidth: '180px'
                  }} title={transcript.originalFileName || transcript.fileName}>
                    {transcript.originalFileName || transcript.fileName}
                  </div>
                </td>
                <td style={{ padding: '8px 10px', fontSize: '12px', color: '#374151', verticalAlign: 'top' }}>
                  {editingCell?.transcriptId === transcript.id && editingCell?.field === 'companyName' ? (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <select
                        value={editValue || ''}
                        onChange={(e) => {
                          setEditValue(e.target.value);
                          // If company is being changed, clear the contact names for this transcript
                          if (e.target.value !== transcript.companyName) {
                            // Find and update the transcript's contact names to empty
                            setTranscripts(prev => prev.map(t => 
                              t.id === transcript.id 
                                ? { ...t, contactNames: [] }
                                : t
                            ));
                          }
                        }}
                        style={{
                          padding: '6px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '11px',
                          backgroundColor: 'white',
                          width: '80px',
                          minHeight: '32px'
                        }}
                        autoFocus
                      >
                        <option value="">-- Select Company --</option>

                        {accounts && Array.isArray(accounts) && accounts.length > 0 ? (
                          [...accounts]
                            .sort((a, b) => {
                              const nameA = (a.companyName || a.name || '').toLowerCase();
                              const nameB = (b.companyName || b.name || '').toLowerCase();
                              return nameA.localeCompare(nameB);
                            })
                            .map((account) => (
                              <option key={account.id} value={account.companyName || account.name}>
                                {account.companyName || account.name || 'Unnamed Company'}
                              </option>
                            ))
                        ) : (
                          <option value="" disabled>Loading companies...</option>
                        )}
                      </select>
                      <button
                        onClick={() => handleSaveEdit(transcript.id, 'companyName')}
                        disabled={savingCell?.transcriptId === transcript.id && savingCell?.field === 'companyName'}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '5px 7px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          cursor: 'pointer',
                          width: '22px',
                          height: '32px'
                        }}
                      >
                        {savingCell?.transcriptId === transcript.id && savingCell?.field === 'companyName' ? '...' : '✓'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        style={{
                          background: '#6b7280',
                          color: 'white',
                          border: 'none',
                          padding: '5px 7px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          cursor: 'pointer',
                          width: '22px',
                          height: '32px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div 
                      style={{ 
                        cursor: 'pointer',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        backgroundColor: transcript.companyName ? '#f0f9ff' : '#f9fafb',
                        border: '1px solid transparent',
                        minHeight: '36px',
                        width: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onClick={() => handleStartEdit(transcript.id, 'companyName', transcript.companyName || '')}
                      title={transcript.companyName ? 'Click to edit' : 'Click to add company name'}
                    >
                      <span style={{ fontSize: '11px', color: transcript.companyName ? '#1e40af' : '#9ca3af' }}>
                        {transcript.companyName || 'Click to add'}
                      </span>
                      <FaEdit style={{ fontSize: '8px', color: '#9ca3af', marginLeft: '4px' }} />
                    </div>
                  )}
                    </td>
                <td style={{ padding: '8px 10px', fontSize: '12px', color: '#374151', verticalAlign: 'top' }}>
                  {editingCell?.transcriptId === transcript.id && editingCell?.field === 'contactNames' ? (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                      <div style={{
                          flex: 1,
                        padding: '6px 8px',
                          border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '11px',
                        backgroundColor: 'white',
                        width: '160px',
                        minHeight: '60px',
                        maxHeight: '120px',
                        overflowY: 'auto'
                      }}>
                        {contactsList.length > 0 ? (
                          [...contactsList]
                            .filter(contact => {
                              // If no company is selected, show all contacts
                              if (!transcript.companyName) return true;
                              // If company is selected, only show contacts from that company
                              return contact.company === transcript.companyName || contact.companyName === transcript.companyName;
                            })
                            .sort((a, b) => {
                              const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
                              const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
                              return nameA.localeCompare(nameB);
                            })
                            .map((contact) => {
                              const contactName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email || 'Unnamed Contact';
                              const isSelected = editValue ? editValue.split(', ').includes(contactName) : false;
                              
                              return (
                                <div key={contact.id} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  padding: '4px 0',
                                  cursor: 'pointer'
                                }}
                                onClick={() => {
                                  const currentNames = editValue ? editValue.split(', ') : [];
                                  if (isSelected) {
                                    // Remove contact
                                    const newNames = currentNames.filter(name => name !== contactName);
                                    setEditValue(newNames.join(', '));
                                  } else {
                                    // Add contact
                                    const newNames = [...currentNames, contactName];
                                    setEditValue(newNames.join(', '));
                                  }
                                }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}} // Handled by onClick on parent div
                                    style={{
                                      marginRight: '6px',
                                      cursor: 'pointer'
                                    }}
                                  />
                                  <span style={{
                                    fontSize: '11px',
                                    color: '#374151',
                                    flex: 1
                      }}>
                                    {contactName}
                      </span>
                                </div>
                              );
                            })
                        ) : (
                          <div style={{ 
                            fontSize: '10px', 
                            color: '#9ca3af', 
                            fontStyle: 'italic',
                            textAlign: 'center',
                            padding: '10px 0'
                          }}>
                            No contacts available
                          </div>
                        )}
                      </div>
                        <button
                        onClick={() => handleSaveEdit(transcript.id, 'contactNames')}
                        disabled={savingCell?.transcriptId === transcript.id && savingCell?.field === 'contactNames'}
                          style={{
                          background: '#10b981',
                            color: 'white',
                            border: 'none',
                          padding: '6px 8px',
                            borderRadius: '4px',
                          fontSize: '11px',
                            cursor: 'pointer',
                          minWidth: '32px',
                          minHeight: '32px'
                          }}
                        >
                        {savingCell?.transcriptId === transcript.id && savingCell?.field === 'contactNames' ? '...' : '✓'}
                        </button>
                        <button
                        onClick={handleCancelEdit}
                          style={{
                          background: '#6b7280',
                            color: 'white',
                            border: 'none',
                          padding: '6px 8px',
                            borderRadius: '4px',
                          fontSize: '11px',
                            cursor: 'pointer',
                          minWidth: '32px',
                          minHeight: '32px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div 
                      style={{ 
                        cursor: 'pointer',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        backgroundColor: Array.isArray(transcript.contactNames) && transcript.contactNames.length > 0 ? '#f0fdf4' : '#f9fafb',
                        border: '1px solid transparent',
                        minHeight: '36px',
                        width: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onClick={() => handleStartEdit(transcript.id, 'contactNames', Array.isArray(transcript.contactNames) ? transcript.contactNames.join(', ') : '')}
                      title={Array.isArray(transcript.contactNames) && transcript.contactNames.length > 0 ? 'Click to edit' : 'Click to add contact names'}
                    >
                      <span style={{ fontSize: '11px', color: Array.isArray(transcript.contactNames) && transcript.contactNames.length > 0 ? '#166534' : '#9ca3af' }}>
                        {Array.isArray(transcript.contactNames) && transcript.contactNames.length > 0 ? transcript.contactNames.join(', ') : 'Click to add'}
                      </span>
                      <FaEdit style={{ fontSize: '8px', color: '#9ca3af', marginLeft: '4px' }} />
                    </div>
                  )}
                </td>
                <td style={{ padding: '8px 10px', fontSize: '12px', color: '#374151', verticalAlign: 'top' }}>
                  <div style={{ 
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: '1.4',
                    maxHeight: '60px',
                    overflow: 'hidden',
                    padding: '4px 6px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '4px',
                    border: '1px solid #e2e8f0'
                  }} title={transcript.summary || 'Analysis in progress...'}>
                    {transcript.summary || 'Analysis in progress...'}
                  </div>
                </td>
                <td style={{ padding: '8px 10px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    <div style={{ 
                      display: 'flex', 
                      gap: '12px', 
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                      justifyContent: 'flex-start'
                    }}>
                                                  <button
                          onClick={() => {
                            handleViewTranscript(transcript);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: deepAnalysisTranscript?.isLoading && deepAnalysisTranscript?.id === transcript.id ? '#9ca3af' : '#2563eb',
                            cursor: deepAnalysisTranscript?.isLoading && deepAnalysisTranscript?.id === transcript.id ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'background 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                          onMouseOver={(e) => e.target.style.background = '#eff6ff'}
                          onMouseOut={(e) => e.target.style.background = 'transparent'}
                          title="View Analysis"
                          disabled={deepAnalysisTranscript?.isLoading && deepAnalysisTranscript?.id === transcript.id}
                        >
                          {deepAnalysisTranscript?.isLoading && deepAnalysisTranscript?.id === transcript.id ? '🔄' : (
                            <>
                              <FaEye style={{ fontSize: '10px' }} />
                              <span>View</span>
                            </>
                          )}
                        </button>
                        
                        {/* HubSpot Call Logging Button */}
                        <button
                          onClick={async () => {
                            console.log('🔍 HubSpot button clicked for transcript:', transcript.id);
                            console.log('🔍 Transcript data:', transcript);
                            
                            // Prepare call confirmation data
                            const contactName = transcript.contactNames?.[0] || 'Unknown Contact';
                            const companyName = transcript.companyName || 'Unknown Company';
                            
                            // Extract actual call duration from analysis data
                            let actualDuration = getCallDurationInMinutes(transcript);
                            
                            // If duration is still default (30), try to fetch cached transcript to calculate from timestamps
                            if (actualDuration === 30 && transcript.id) {
                              try {
                                const cachedTranscript = await getCachedTranscript(transcript.id);
                                if (cachedTranscript) {
                                  const durationFromCache = getCallDurationInMinutes(cachedTranscript);
                                  if (durationFromCache !== 30) {
                                    actualDuration = durationFromCache;
                                    console.log('✅ Duration calculated from cached transcript:', actualDuration);
                                  }
                                }
                              } catch (error) {
                                console.log('⚠️ Could not fetch cached transcript for duration calculation:', error);
                              }
                            }
                            
                            setCallConfirmationData({
                              transcriptId: transcript.id,
                              contactId: transcript.contactIds?.[0],
                              companyId: transcript.companyId,
                              callTitle: `Call Analysis - ${contactName}`,
                              callNotes: transcript.analysisData ? hubspotService.buildDetailedCallNotes(transcript, transcript.analysisData) : (transcript.summary || 'Call analysis summary'),
                              callDuration: actualDuration.toString(),
                              callOutcome: 'completed'
                            });
                            
                            setShowCallConfirmation(true);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#ff7a59',
                            cursor: 'pointer',
                            fontSize: '12px',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'background 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                          onMouseOver={(e) => (e.target.style.background = '#fff5f5')}
                          onMouseOut={(e) => (e.target.style.background = 'transparent')}
                          title="Log Call Analysis to HubSpot"
                        >
                          <>
                            <span>📞</span>
                            <span>Log Call</span>
                          </>
                        </button>
                        
                      <button
                        onClick={() => {
                          handleDeleteTranscript(transcript.id);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '12px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'background 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}
                        onMouseOver={(e) => e.target.style.background = '#fef2f2'}
                        onMouseOut={(e) => e.target.style.background = 'transparent'}
                          title="Delete"
                        >
                          <FaTrash style={{ fontSize: '10px' }} />
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
              );
              })}
              </tbody>
            </table>
          


          </div>
        </>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px', 
            color: '#6b7280'
          }}>
            <p style={{ margin: '0', fontSize: '14px' }}>
              No transcripts uploaded yet. Upload your first transcript to get started.
            </p>
          </div>
        )}
    </div>

    {/* Deep Analysis Modal */}
    {showDeepAnalysis && deepAnalysisTranscript && (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2000,
      padding: '20px'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: '0', fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
              Deep Analysis: {deepAnalysisTranscript.fileName}
              {deepAnalysisTranscript.isLoading && (
                <span style={{ fontSize: '16px', color: '#6b7280', marginLeft: '8px' }}>
                  🔄 Loading...
                </span>
              )}
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
              Uploaded on {new Date(deepAnalysisTranscript.createdAt).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={() => {
      setShowDeepAnalysis(false);
      setDeepAnalysisTranscript(null);
    }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px',
              borderRadius: '4px',
              transition: 'background 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
            onMouseOut={(e) => e.target.style.background = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1
        }}>
          {/* Summary Section */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
              Executive Summary
            </h3>
            <p style={{ margin: '0', fontSize: '16px', color: '#475569', lineHeight: '1.6' }}>
              {deepAnalysisTranscript.summary}
            </p>
          </div>

          {/* Sentiment Analysis */}
          {deepAnalysisTranscript.analysisData ? (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Sentiment Analysis
              </h3>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                borderRadius: '20px',
                background: deepAnalysisTranscript.analysisData.sentiment === 'positive' ? '#dcfce7' : 
                          deepAnalysisTranscript.analysisData.sentiment === 'negative' ? '#fee2e2' : '#fef3c7',
                color: deepAnalysisTranscript.analysisData.sentiment === 'positive' ? '#166534' : 
                        deepAnalysisTranscript.analysisData.sentiment === 'negative' ? '#991b1b' : '#92400e',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {deepAnalysisTranscript.analysisData.sentiment.charAt(0).toUpperCase() + deepAnalysisTranscript.analysisData.sentiment.slice(1)} Sentiment
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Analysis Status
              </h3>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                borderRadius: '20px',
                background: '#fef3c7',
                color: '#92400e',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Analysis in Progress...
              </div>
            </div>
          )}

          {/* Key Topics */}
          {deepAnalysisTranscript.analysisData ? (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Key Topics Discussed
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {deepAnalysisTranscript.analysisData.keyTopics.map((topic, index) => (
                  <span key={index} style={{
                    padding: '6px 12px',
                    background: '#f1f5f9',
                    color: '#334155',
                    borderRadius: '16px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Key Topics
              </h3>
              <p style={{ margin: '0', fontSize: '16px', color: '#6b7280', fontStyle: 'italic' }}>
                Analysis in progress... Topics will appear here once processing is complete.
              </p>
            </div>
          )}

          {/* Action Items */}
          {deepAnalysisTranscript.analysisData ? (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Action Items
              </h3>
              <ul style={{ margin: '0', paddingLeft: '20px' }}>
                {deepAnalysisTranscript.analysisData.actionItems.map((item, index) => (
                  <li key={index} style={{
                    marginBottom: '8px',
                    fontSize: '16px',
                    color: '#475569',
                    lineHeight: '1.5'
                  }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Action Items
              </h3>
              <p style={{ margin: '0', fontSize: '16px', color: '#6b7280', fontStyle: 'italic' }}>
                Analysis in progress... Action items will appear here once processing is complete.
              </p>
            </div>
          )}

          {/* Risk Factors */}
          {deepAnalysisTranscript.analysisData ? (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Risk Factors
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {deepAnalysisTranscript.analysisData.riskFactors.map((risk, index) => (
                  <span key={index} style={{
                    padding: '6px 12px',
                    background: '#fef2f2',
                    color: '#991b1b',
                    borderRadius: '16px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    ⚠️ {risk}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Risk Factors
              </h3>
              <p style={{ margin: '0', fontSize: '16px', color: '#6b7280', fontStyle: 'italic' }}>
                Analysis in progress... Risk factors will appear here once processing is complete.
              </p>
            </div>
          )}

          {/* Call Metrics */}
          {deepAnalysisTranscript.analysisData ? (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Call Metrics
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                <div style={{
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Duration</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                    {(() => {
                      const duration = deepAnalysisTranscript.analysisData.callMetrics.duration;
                      if (typeof duration === 'string' && (duration.includes('min') || duration.includes('hour') || duration.includes('sec'))) {
                        return duration;
                      }
                      // If it's a number or string without unit, add 'min'
                      return `${duration} min`;
                    })()}
                  </div>
                </div>
                <div style={{
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Participants</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                    {deepAnalysisTranscript.analysisData.callMetrics.participantCount}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Call Metrics
              </h3>
              <p style={{ margin: '0', fontSize: '16px', color: '#6b7280', fontStyle: 'italic' }}>
                Analysis in progress... Call metrics will appear here once processing is complete.
              </p>
            </div>
          )}

          {/* Next Steps */}
          {deepAnalysisTranscript.analysisData ? (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Next Steps
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                    Immediate (Next 24 hours)
                  </h4>
                  <ul style={{ margin: '0', paddingLeft: '20px' }}>
                    {deepAnalysisTranscript.analysisData.nextSteps.immediate.map((step, index) => (
                      <li key={index} style={{
                        marginBottom: '4px',
                        fontSize: '14px',
                        color: '#475569'
                      }}>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                    Short Term (This week)
                  </h4>
                  <ul style={{ margin: '0', paddingLeft: '20px' }}>
                    {deepAnalysisTranscript.analysisData.nextSteps.shortTerm.map((step, index) => (
                      <li key={index} style={{
                        marginBottom: '4px',
                        fontSize: '14px',
                        color: '#475569'
                      }}>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                    Long Term (This quarter)
                  </h4>
                  <ul style={{ margin: '0', paddingLeft: '20px' }}>
                    {deepAnalysisTranscript.analysisData.nextSteps.longTerm.map((step, index) => (
                      <li key={index} style={{
                        marginBottom: '4px',
                        fontSize: '14px',
                        color: '#475569'
                      }}>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Next Steps
              </h3>
              <p style={{ margin: '0', fontSize: '16px', color: '#6b7280', fontStyle: 'italic' }}>
                Analysis in progress... Next steps will appear here once processing is complete.
              </p>
            </div>
          )}

          {/* Key Insights */}
          {deepAnalysisTranscript.analysisData ? (
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Key Insights
              </h3>
              <div style={{
                padding: '16px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <p style={{ margin: '0', fontSize: '16px', color: '#475569', lineHeight: '1.6' }}>
                  {deepAnalysisTranscript.analysisData.insights}
                </p>
              </div>
            </div>
          ) : (
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                Key Insights
              </h3>
              <p style={{ margin: '0', fontSize: '16px', color: '#6b7280', fontStyle: 'italic' }}>
                Analysis in progress... Key insights will appear here once processing is complete.
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
    )}

    {/* HubSpot Call Confirmation Modal */}
    {showCallConfirmation && (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1e293b' }}>
          Log Call to HubSpot
        </h3>
        <button
          onClick={() => setShowCallConfirmation(false)}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px'
          }}
        >
          ×
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
          Review and edit the call details before logging to HubSpot:
        </p>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
          Call Title *
        </label>
        <input
          type="text"
          value={callConfirmationData.callTitle}
          onChange={(e) => setCallConfirmationData(prev => ({ ...prev, callTitle: e.target.value }))}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
          placeholder="Enter call title"
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
          Call Notes *
        </label>
        <textarea
          value={callConfirmationData.callNotes}
          onChange={(e) => setCallConfirmationData(prev => ({ ...prev, callNotes: e.target.value }))}
          rows={6}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            resize: 'vertical'
          }}
          placeholder="Enter call notes and analysis"
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
          Call Outcome
        </label>
        <select
          value={callConfirmationData.callOutcome}
          onChange={(e) => setCallConfirmationData(prev => ({ ...prev, callOutcome: e.target.value }))}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        >
          <option value="completed">Completed</option>
          <option value="scheduled">Scheduled</option>
          <option value="no_answer">No Answer</option>
          <option value="left_message">Left Message</option>
          <option value="not_interested">Not Interested</option>
        </select>
      </div>

      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '24px'
      }}>
        <button
          onClick={() => setShowCallConfirmation(false)}
          style={{
            padding: '8px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            background: 'white',
            color: '#374151',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            try {
              setLoggingCall(true);
              
              const result = await hubspotService.logCallWithTranscript(
                callConfirmationData.transcriptId,
                callConfirmationData.contactId,
                callConfirmationData.companyId,
                true // logToBoth
              );
              
              showSuccess('Call logged successfully in HubSpot!', {
                title: 'HubSpot Integration',
                duration: 3000
              });
              setShowCallConfirmation(false);
              
            } catch (error) {
              console.error('Error logging call:', error);
              showError(error.response?.data?.message || 'Failed to log call to HubSpot', {
                title: 'HubSpot Integration Error',
                duration: 5000
              });
            } finally {
              setLoggingCall(false);
            }
          }}
          disabled={loggingCall || !callConfirmationData.callTitle || !callConfirmationData.callNotes}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            background: loggingCall ? '#9ca3af' : '#ff7a59',
            color: 'white',
            fontSize: '14px',
            cursor: loggingCall ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          {loggingCall ? (
            <>
              <div style={{
                width: '12px',
                height: '12px',
                border: '2px solid transparent',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Logging...
            </>
          ) : (
            <>
              Log Call to HubSpot
            </>
          )}
        </button>
      </div>
      </div>
    </div>
  )}
    </div>
  );
});

export default RadarTranscriptAnalyzer;

RadarTranscriptAnalyzer.displayName = 'RadarTranscriptAnalyzer';
